import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../../config';

/** Sets httpOnly cookie on the API origin so Bull Board at /admin/jobs loads in the browser (production). */
export const establishBullBoardSession = createAsyncThunk(
  'jobs/bullBoardSession',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      const res = await fetch(`${API_BASE_URL}/api/jobs/admin/bull-board-session`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    } catch (e) {
      return rejectWithValue(e.message || 'Could not start Bull Board session');
    }
  }
);

export const fetchJobOverview = createAsyncThunk(
  'jobs/fetchOverview',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      const res = await fetch(`${API_BASE_URL}/api/jobs/admin/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data.data;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to load job overview');
    }
  }
);

/** body: { jobName: string, data?: object } */
export const triggerJob = createAsyncThunk(
  'jobs/trigger',
  async ({ jobName, data }, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      const res = await fetch(`${API_BASE_URL}/api/jobs/admin/trigger`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jobName, ...(data !== undefined ? { data } : {}) }),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || `HTTP ${res.status}`);
      return { jobName, ...payload.data };
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to trigger job');
    }
  }
);

const jobsSlice = createSlice({
  name: 'jobs',
  initialState: {
    error: null,
    bullBoardOpening: false,
    overview: null,
    overviewLoading: false,
    triggerLoading: {},
  },
  reducers: {
    clearJobsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(establishBullBoardSession.pending, (state) => {
        state.bullBoardOpening = true;
        state.error = null;
      })
      .addCase(establishBullBoardSession.fulfilled, (state) => {
        state.bullBoardOpening = false;
      })
      .addCase(establishBullBoardSession.rejected, (state, { payload }) => {
        state.bullBoardOpening = false;
        state.error = payload || 'Could not open queue monitor';
      })
      .addCase(fetchJobOverview.pending, (state) => {
        state.overviewLoading = true;
        state.error = null;
      })
      .addCase(fetchJobOverview.fulfilled, (state, { payload }) => {
        state.overviewLoading = false;
        state.overview = payload;
      })
      .addCase(fetchJobOverview.rejected, (state, { payload }) => {
        state.overviewLoading = false;
        state.error = payload || 'Failed to load jobs';
      })
      .addCase(triggerJob.pending, (state, { meta }) => {
        state.triggerLoading[meta.arg.jobName] = true;
        state.error = null;
      })
      .addCase(triggerJob.fulfilled, (state, { meta }) => {
        state.triggerLoading[meta.arg.jobName] = false;
      })
      .addCase(triggerJob.rejected, (state, { payload, meta }) => {
        state.triggerLoading[meta.arg.jobName] = false;
        state.error = payload || 'Trigger failed';
      });
  },
});

export const { clearJobsError } = jobsSlice.actions;
export const selectJobsError = (state) => state.jobs.error;
export const selectBullBoardOpening = (state) => state.jobs.bullBoardOpening;
export const selectJobOverview = (state) => state.jobs.overview;
export const selectJobOverviewLoading = (state) => state.jobs.overviewLoading;
export const selectTriggerLoading = (state) => state.jobs.triggerLoading;

export default jobsSlice.reducer;
